/**
 * Loan Calculator Utilities
 * Functions for calculating EMI, amortization schedules, and loan repayment details
 */

export interface LoanInput {
  principal: number; // Loan amount (current balance)
  annualInterestRate: number; // Annual interest rate in percentage (e.g., 10.5)
  tenureMonths: number; // Loan tenure in months
  emiAmount?: number; // Optional: if user wants to override calculated EMI
}

export interface ExtraPayment {
  month: number; // Which month the extra payment is made
  amount: number; // Extra payment amount
}

export interface MonthlyPayment {
  month: number;
  emiAmount: number;
  principalAmount: number;
  interestAmount: number;
  extraPayment: number;
  totalPayment: number;
  remainingBalance: number;
  date?: string; // Optional payment date
}

export interface LoanSummary {
  principal: number;
  monthlyEMI: number;
  totalMonths: number;
  totalPayment: number;
  totalInterest: number;
  totalExtraPayments: number;
  actualTenure: number; // Actual months to repay (considering extra payments)
  debtFreeDate: string;
  monthlySavings: number; // Interest saved with extra payments
}

/**
 * Calculate EMI (Equated Monthly Installment) using the standard formula
 * EMI = [P x R x (1+R)^N] / [(1+R)^N-1]
 * Where:
 * P = Principal loan amount
 * R = Monthly interest rate (annual rate / 12 / 100)
 * N = Loan tenure in months
 */
export function calculateEMI(
  principal: number,
  annualInterestRate: number,
  tenureMonths: number,
): number {
  if (principal <= 0 || tenureMonths <= 0) {
    return 0;
  }

  // If interest rate is 0, simple division
  if (annualInterestRate === 0) {
    return principal / tenureMonths;
  }

  const monthlyRate = annualInterestRate / 12 / 100;
  const numerator = principal * monthlyRate * (1 + monthlyRate) ** tenureMonths;
  const denominator = (1 + monthlyRate) ** tenureMonths - 1;

  return numerator / denominator;
}

/**
 * Generate detailed amortization schedule
 * Shows month-by-month breakdown of EMI, principal, interest, and remaining balance
 */
export function generateAmortizationSchedule(
  loanInput: LoanInput,
  extraPayments: ExtraPayment[] = [],
  startDate?: Date,
): MonthlyPayment[] {
  const { principal, annualInterestRate, tenureMonths } = loanInput;

  // Calculate EMI (use provided or calculate)
  const emiAmount =
    loanInput.emiAmount || calculateEMI(principal, annualInterestRate, tenureMonths);

  if (emiAmount === 0) {
    return [];
  }

  const schedule: MonthlyPayment[] = [];
  let remainingBalance = principal;
  const monthlyRate = annualInterestRate / 12 / 100;

  // Create a map of extra payments for quick lookup
  const extraPaymentMap = new Map<number, number>();
  for (const ep of extraPayments) {
    extraPaymentMap.set(ep.month, ep.amount);
  }

  let month = 1;
  const currentDate = startDate || new Date();

  // Generate schedule until loan is paid off
  while (remainingBalance > 0.01 && month <= tenureMonths * 2) {
    // Max 2x original tenure as safety
    // Calculate interest for this month
    const interestAmount = remainingBalance * monthlyRate;

    // Principal portion of EMI
    let principalAmount = Math.min(emiAmount - interestAmount, remainingBalance);

    // Ensure we don't pay more than remaining balance
    let actualEMI = Math.min(emiAmount, remainingBalance + interestAmount);

    // Handle last payment (might be less than full EMI)
    if (remainingBalance <= emiAmount - interestAmount) {
      principalAmount = remainingBalance;
      actualEMI = principalAmount + interestAmount;
    }

    // Check for extra payment this month
    const extraPayment = extraPaymentMap.get(month) || 0;

    // Apply extra payment to principal
    const totalPrincipal = Math.min(principalAmount + extraPayment, remainingBalance);
    const totalPayment = actualEMI + extraPayment;

    // Update remaining balance
    remainingBalance = Math.max(0, remainingBalance - totalPrincipal);

    // Calculate payment date
    const paymentDate = new Date(currentDate);
    paymentDate.setMonth(currentDate.getMonth() + month - 1);

    schedule.push({
      month,
      emiAmount: actualEMI,
      principalAmount: totalPrincipal - extraPayment,
      interestAmount,
      extraPayment,
      totalPayment,
      remainingBalance,
      date: paymentDate.toISOString().split("T")[0],
    });

    month++;
  }

  return schedule;
}

/**
 * Calculate loan summary with all key metrics
 */
export function calculateLoanSummary(
  loanInput: LoanInput,
  extraPayments: ExtraPayment[] = [],
  startDate?: Date,
): LoanSummary {
  const schedule = generateAmortizationSchedule(loanInput, extraPayments, startDate);

  const totalPayment = schedule.reduce((sum, payment) => sum + payment.totalPayment, 0);
  const totalInterest = schedule.reduce((sum, payment) => sum + payment.interestAmount, 0);
  const totalExtraPayments = schedule.reduce((sum, payment) => sum + payment.extraPayment, 0);
  const actualTenure = schedule.length;

  // Calculate EMI
  const monthlyEMI =
    loanInput.emiAmount ||
    calculateEMI(loanInput.principal, loanInput.annualInterestRate, loanInput.tenureMonths);

  // Calculate debt-free date
  const currentDate = startDate || new Date();
  const debtFreeDate = new Date(currentDate);
  debtFreeDate.setMonth(currentDate.getMonth() + actualTenure);

  // Calculate interest saved with extra payments
  const scheduleWithoutExtra = generateAmortizationSchedule(loanInput, [], startDate);
  const interestWithoutExtra = scheduleWithoutExtra.reduce(
    (sum, payment) => sum + payment.interestAmount,
    0,
  );
  const monthlySavings = interestWithoutExtra - totalInterest;

  return {
    principal: loanInput.principal,
    monthlyEMI,
    totalMonths: loanInput.tenureMonths,
    totalPayment,
    totalInterest,
    totalExtraPayments,
    actualTenure,
    debtFreeDate: debtFreeDate.toISOString().split("T")[0],
    monthlySavings,
  };
}

/**
 * Calculate how much interest is saved by making extra payments
 */
export function calculateInterestSavings(
  loanInput: LoanInput,
  extraPayments: ExtraPayment[],
): {
  savedInterest: number;
  monthsSaved: number;
  originalInterest: number;
  newInterest: number;
} {
  const originalSchedule = generateAmortizationSchedule(loanInput, []);
  const newSchedule = generateAmortizationSchedule(loanInput, extraPayments);

  const originalInterest = originalSchedule.reduce((sum, p) => sum + p.interestAmount, 0);
  const newInterest = newSchedule.reduce((sum, p) => sum + p.interestAmount, 0);

  return {
    savedInterest: originalInterest - newInterest,
    monthsSaved: originalSchedule.length - newSchedule.length,
    originalInterest,
    newInterest,
  };
}
